// frontend/src/components/wizard/StepMapping/MappingTable.jsx
import React, { useMemo } from 'react';
import { Database, FileSpreadsheet, Settings2, AlertTriangle, Pin, Calendar, ShieldCheck } from 'lucide-react';
import { SearchableSelect } from './SearchableSelect';
import { getIncompleteRulesCount } from '../../../utils/etlMappingUtils';
import { useLanguageStore } from '../../../store/useLanguageStore';

export const MappingTable = ({
  targetColumns, sourceColumns, localMap, target,
  activeTarget, setActiveTarget, setActiveTransform,
  handleAliasChange, handleTabNavigation, handleSelectChange,
  syncMode, targetRelationships,
  onOpenQualityRules,
}) => {

  // Columnas IGNORED de tablas padre FK — candidatas a new_id_ref
  const newIdRefOptions = React.useMemo(() => {
    if (!targetRelationships?.length) return [];
    return targetRelationships
      .filter(r => r.joinType === 'FK' && r.anchorSource && r.bridgeColumn)
      .map(r => ({
        value: `__NEW_ID__:${r.tableFrom}.${r.columnFrom}`,
        label: `${r.tableFrom}.${r.columnFrom}`,
        table: r.tableFrom,
        column: r.columnFrom,
      }));
  }, [targetRelationships]);

  const { t } = useLanguageStore();

  const isTargetFlatFile = ['csv', 'excel', 'googlesheets'].includes(target?.type?.toLowerCase());
  const isMongo = target?.type?.toLowerCase() === 'mongodb';
  const isEditableDest = isTargetFlatFile || isMongo;

  // ── Grupos de opciones para SearchableSelect ─────────────────────
  const selectGroups = useMemo(() => {
    const groups = [];

    // Acciones fijas
    groups.push({
      label: null,
      options: [
        { value: '', label: t('wizard.mapping.unmapped') || '-- Sin Mapear --' },
        { value: 'IGNORE', label: t('wizard.mapping.ignore_in_target') || 'Ignorar en Destino', icon: '🚫' },
      ],
    });

    // IDs Auto-generados (new_id_ref)
    if (newIdRefOptions.length > 0) {
      groups.push({
        label: t('wizard.mapping.new_id_ref_group') || '⚡ ID Auto-generado (Nuevo)',
        options: newIdRefOptions.map(opt => ({ value: opt.value, label: opt.label })),
      });
    }

    // Columnas origen (agrupadas por tabla en N:M, grupo único en 1:1)
    const defaultGroup = t('wizard.mapping.source_columns') || 'Columnas de Origen';
    const srcGroups = sourceColumns.reduce((acc, col) => {
      const grp = col.name.includes('.') ? col.name.split('.')[0] : defaultGroup;
      if (!acc[grp]) acc[grp] = [];
      acc[grp].push({ value: col.name, label: col.name });
      return acc;
    }, {});

    Object.entries(srcGroups).forEach(([grpLabel, opts]) => {
      groups.push({ label: grpLabel, options: opts });
    });

    return groups;
  }, [sourceColumns, newIdRefOptions, t]);

  const getWarnings = (tColObj, sColObj, confType, pipeline) => {
    let warns = [];
    if (isTargetFlatFile) return warns;

    if (confType === 'ignore' && !tColObj.nullable && !tColObj.autoincrement) {
      warns.push(t('wizard.mapping.warnings.mandatory') || "Destino obligatorio (NOT NULL). Ignorar causará error.");
    }

    if (sColObj && tColObj.type && sColObj.type && confType !== 'ignore') {
      const sType = sColObj.type.toLowerCase();
      const tType = tColObj.type.toLowerCase();
      const isSourceText = sType.includes('char') || sType.includes('text');
      const isTargetNum = tType.includes('int') || tType.includes('dec') || tType.includes('float') || tType.includes('num') || tType.includes('long');

      if (isSourceText && isTargetNum) warns.push(t('wizard.mapping.warnings.type_mismatch') || "Posible error: Intentas enviar Texto a un campo Numérico.");
    }

    if (pipeline && pipeline.length > 0 && tColObj.type) {
      const tType = tColObj.type.toLowerCase();
      const isTargetNum = tType.includes('int') || tType.includes('dec') || tType.includes('float') || tType.includes('num');

      const hasTextRule = pipeline.some(rule => {
        const ruleName = rule.id || rule.action || '';
        return ['ADD_PREFIX', 'ADD_SUFFIX', 'PREFIX', 'SUFFIX'].includes(ruleName);
      });
      if (isTargetNum && hasTextRule) {
        warns.push(t('wizard.mapping.warnings.prefix_num') || "⚠️ Cuidado: Añadir texto (Prefijo/Sufijo) a un campo Numérico causará que la Base de Datos lo rechace.");
      }

      const fixedRule = pipeline.find(rule => (rule.id || rule.action) === 'SET_FIXED');
      if (isTargetNum && fixedRule) {
        const fixedVal = fixedRule.params?.value;
        if (fixedVal !== undefined && fixedVal !== null && fixedVal !== '' && isNaN(Number(fixedVal))) {
          warns.push(t('wizard.mapping.warnings.fixed_type_mismatch') || "⚠️ El valor fijo no es numérico, pero el campo destino es de tipo numérico. La Base de Datos lo rechazará.");
        }
      }
    }
    // 🟢 NUEVA REGLA: Alerta si es Upsert, es Llave Primaria y el usuario no la mapea
    if (syncMode === 'upsert' && tColObj.is_pk && (confType === 'unmapped' || confType === 'ignore')) {
      warns.push(t('wizard.mapping.warnings.missing_pk_upsert') || "⚠️ Cuidado: 'Upsert' requiere mapear la PK.");
    }
    return warns;
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="grid grid-cols-12 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
        <div className="col-span-5 p-2 flex items-center gap-2"><Database className="w-4 h-4 text-emerald-600 hidden md:block" /> <span className="truncate">{t('wizard.mapping.target') || 'Destino'}</span></div>
        <div className="col-span-1 p-2 flex justify-center items-center border-l border-slate-200">
          <span className="hidden md:inline">{t('wizard.mapping.rule') || 'Regla'}</span><Settings2 className="w-4 h-4 md:hidden text-slate-400" />
        </div>
        <div className="col-span-6 p-2 flex items-center gap-2 border-l border-slate-200"><FileSpreadsheet className="w-4 h-4 text-indigo-600 hidden md:block" /> <span className="truncate">{t('wizard.mapping.source') || 'Origen'}</span></div>
      </div>

      <div className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto custom-scrollbar pb-32">
        {targetColumns.map((tColObj, idx) => {
          const tColName = tColObj.name;
          const conf = localMap[tColName] || { type: 'unmapped', pipeline: [] };
          const isActive = activeTarget === tColName;
          const incompletes = getIncompleteRulesCount(conf.pipeline);
          const hasFixedRule = conf.pipeline?.find(r => r.id === 'SET_FIXED');
          const hasDateGenRule = !hasFixedRule && conf.pipeline?.find(r => r.id === 'SET_CURRENT_DATE' || r.id === 'DATE_NOW');
          const sColObj = sourceColumns.find(c => c.name === conf.source);
          const warnings = getWarnings(tColObj, sColObj, conf.type, conf.pipeline);

          let rowStyle = "bg-amber-50/20 hover:bg-amber-50";
          let iconColor = "text-amber-300";
          if (conf.type === 'direct') { rowStyle = "bg-white hover:bg-emerald-50/30"; iconColor = "text-emerald-500"; }
          else if (conf.type === 'transform' || hasFixedRule) { rowStyle = "bg-purple-50/20 hover:bg-purple-50"; iconColor = "text-purple-500"; }
          if (conf.type === 'ignore') { rowStyle = "bg-slate-50 hover:bg-slate-100 opacity-60"; iconColor = "text-slate-400"; }
          if (incompletes > 0) { rowStyle = "bg-red-50/30 hover:bg-red-50"; iconColor = "text-red-500"; }
          if (warnings.length > 0 && conf.type === 'ignore') { rowStyle = "bg-red-50/50 border-l-4 border-l-red-500"; }
          if (isActive) rowStyle = "bg-blue-50 ring-2 ring-blue-400 z-10 relative shadow-md";

          return (
            <div key={idx} className={`grid grid-cols-12 group transition-all duration-200 ${rowStyle} items-center`}>
              <div className="col-span-5 py-2 px-2 md:px-3 flex items-center">
                <div className="flex flex-col w-full">
                  <div className="flex items-center gap-1.5">
                    {!isTargetFlatFile && tColObj.is_pk && <span title={t('wizard.mapping.primary_key')} className="text-[10px]">🔑</span>}
                    {!isTargetFlatFile && tColObj.is_fk && <span title={t('wizard.mapping.foreign_key')} className="text-[10px]">🔗</span>}
                    {!isTargetFlatFile && tColObj.autoincrement && (
                      <span className="bg-purple-100 text-purple-700 text-[9px] font-black px-1.5 py-0.5 rounded uppercase" title={t('wizard.mapping.auto_increment')}>AUTO</span>
                    )}

                    {isEditableDest ? (
                      <div className="flex flex-col w-full">
                        <input
                          type="text"
                          data-alias-idx={idx}
                          key={tColName}
                          defaultValue={tColName}
                          onBlur={(e) => {
                            const newVal = e.target.value.trim();
                            if (newVal && newVal !== tColName)
                              handleAliasChange(tColName, newVal);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                            handleTabNavigation(e, idx);
                          }}
                          placeholder={t('wizard.mapping.header_name')}
                          className={`text-[12px] font-mono font-bold w-full bg-transparent border-b outline-none transition-colors ${conf.type === 'ignore'
                            ? 'line-through text-slate-400 border-transparent'
                            : 'text-slate-800 border-slate-300 focus:border-indigo-500'
                            }`}
                          disabled={conf.type === 'ignore'}
                        />
                        {isMongo && tColObj.type && tColObj.type !== 'ANY' && (
                          <div className="flex items-center mt-1 opacity-80">
                            <span className="text-[9px] font-mono text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded uppercase">
                              [{tColObj.type}]
                            </span>
                            {!tColObj.nullable && (
                              <span className="text-red-500 font-bold text-[11px] ml-1" title={t('wizard.mapping.mandatory_field')}>*</span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (() => {
                      // 🔧 N:M: "tabla.campo" → tabla arriba (small/muted) + campo abajo
                      const hasDot = tColName.includes('.');
                      const tblPart = hasDot ? tColName.split('.')[0] : null;
                      const colPart = hasDot ? tColName.split('.').slice(1).join('.') : tColName;
                      return (
                        <div className="flex flex-col min-w-0">
                          {hasDot && (
                            <span className="text-[9px] font-mono text-indigo-400 truncate leading-tight">
                              {tblPart}
                            </span>
                          )}
                          <span className={`text-[12px] font-mono font-medium truncate leading-tight
                            ${conf.type === 'ignore' ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                            {colPart}
                            {/* 🔧 length movido a la fila de tipo, no va aquí */}
                          </span>
                        </div>
                      );
                    })()}

                    {!isEditableDest && !tColObj.nullable && !tColObj.autoincrement && <span title={t('wizard.mapping.mandatory_field')} className="text-red-500 font-bold ml-1">*</span>}

                    {warnings.length > 0 && conf.type === 'ignore' && (
                      <div className="group/warn relative">
                        <AlertTriangle className="w-4 h-4 text-red-500 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-1 hidden group-hover/warn:block w-48 bg-slate-800 text-white text-[10px] p-2 rounded shadow-lg z-50">
                          {warnings.map((w, i) => <p key={i}>{w}</p>)}
                        </div>
                      </div>
                    )}
                  </div>

                  {!isEditableDest && (
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {tColObj.type && (
                        <span className="text-[9px] text-slate-400 font-mono uppercase">
                          [{tColObj.type}
                          {/* 🔧 length después del tipo */}
                          {tColObj.length ? `(${tColObj.length})` : ''}]
                        </span>
                      )}
                      {tColObj.nullable && !tColObj.is_pk && (
                        <span className="text-[9px] text-slate-400 font-mono font-bold bg-slate-100 px-1.5 py-0.5 rounded">
                          NULL
                        </span>
                      )}
                      {!tColObj.nullable && !tColObj.autoincrement && !tColObj.is_pk && (
                        <span className="text-[9px] text-red-500 font-mono font-bold bg-red-50 px-1.5 py-0.5 rounded">
                          {t('wizard.mapping.required') || 'REQUIRED'}
                        </span>
                      )}
                      {tColObj.default_val && (
                        <span className="text-[9px] text-indigo-500 font-mono font-bold bg-indigo-50 px-1.5 py-0.5 rounded">
                          Default: {tColObj.default_val}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-1 py-1 px-1 border-l border-slate-100 flex justify-center items-center gap-0.5 relative">
                <button onClick={() => setActiveTransform(tColName)} disabled={conf.type === 'ignore'} className={`p-1.5 rounded-lg border transition-all relative ${conf.type === 'ignore' ? 'opacity-30 cursor-not-allowed border-transparent' : conf.type !== 'unmapped' && conf.type !== 'direct' ? 'bg-white border-slate-300 shadow-sm hover:shadow-md' : 'border-transparent hover:bg-white hover:border-slate-200'}`}>
                  <Settings2 className={`w-4 h-4 ${iconColor}`} />
                  {incompletes > 0 && conf.type !== 'ignore' && <span title={`${incompletes} ${t('wizard.mapping.rules_missing')}`} className="absolute -top-1 -left-1 bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm animate-pulse">{incompletes}</span>}
                  {conf.pipeline?.length > 0 && conf.type !== 'ignore' && <span title={`${conf.pipeline.length} ${t('wizard.mapping.rules_active')}`} className="absolute -top-1 -right-1 bg-purple-600 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">{conf.pipeline.length}</span>}
                </button>
                {/* 📏 S4 Data Rules */}
                <button
                  onClick={() => onOpenQualityRules?.(tColName)}
                  disabled={conf.type === 'ignore'}
                  className={`p-1 rounded-lg transition-all relative ${conf.type === 'ignore'
                      ? 'opacity-30 cursor-not-allowed'
                      : conf.qualityRules?.length > 0
                        ? 'bg-emerald-50 hover:bg-emerald-100'
                        : 'hover:bg-slate-100'
                    }`}
                  title={t('data_rules.edit_rules')}
                >
                  <ShieldCheck className={`w-3.5 h-3.5 ${conf.qualityRules?.length > 0 ? 'text-emerald-600' : 'text-slate-300'}`} />
                  {conf.qualityRules?.length > 0 && conf.type !== 'ignore' && (
                    <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[8px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center shadow-sm">
                      {conf.qualityRules.length}
                    </span>
                  )}
                </button>
              </div>

              <div className="col-span-6 py-1 px-2 md:px-3 border-l border-slate-100 relative flex flex-col justify-center">
                {hasFixedRule && conf.type !== 'ignore' ? (
                  <div className="w-full flex items-center bg-blue-50 border border-blue-200 rounded-md py-1 px-2">
                    <Pin className="w-3.5 h-3.5 text-blue-500 mr-2 shrink-0" />
                    <span className="text-[11px] md:text-[12px] font-mono font-bold text-blue-800 truncate">{t('wizard.mapping.fixed')}: {hasFixedRule.params.value || t('wizard.mapping.empty')}</span>
                  </div>
                ) : hasDateGenRule && conf.type !== 'ignore' ? (
                  <div className="w-full flex items-center bg-amber-50 border border-amber-200 rounded-md py-1 px-2">
                    <Calendar className="w-3.5 h-3.5 text-amber-600 mr-2 shrink-0" />
                    <span className="text-[11px] md:text-[12px] font-mono font-bold text-amber-800 truncate">
                      {hasDateGenRule.id === 'SET_CURRENT_DATE'
                        ? `${t('wizard.mapping.current_date')}: ${hasDateGenRule.params?.dateFormat || 'YYYY-MM-DD'}`
                        : t('wizard.mapping.date_now')
                      }
                    </span>
                  </div>
                ) : (
                  <div className="w-full relative flex flex-col justify-center">
                    {/* {conf.confidence && conf.type !== 'ignore' && <div className="mb-0.5 md:mb-0 md:absolute md:right-8 md:top-1/2 md:-translate-y-1/2 z-10"><span className={`text-[9px] md:text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${conf.confidence >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : conf.confidence >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{conf.confidence}% {t('wizard.mapping.ai_label') || 'IA'}</span></div>} */}
                    {/* 🔧 Prefijo de tabla para columna fuente — igual que target */}
                    {/* {conf.source && conf.source.includes('.') && conf.type !== 'ignore' && (
                      <span className="text-[9px] font-mono text-indigo-400 truncate leading-tight block mb-0.5">
                        {conf.source.split('.')[0]}
                      </span>
                    )} */}
                    <SearchableSelect
                      value={
                        conf.type === 'new_id_ref'
                          ? `__NEW_ID__:${conf.source}`
                          : conf.type === 'ignore'
                            ? 'IGNORE'
                            : (conf.source || '')
                      }
                      onChange={(val) => handleSelectChange(tColName, val)}
                      groups={selectGroups}
                      displayLabel={
                        conf.type === 'ignore'
                          ? `🚫 ${t('wizard.mapping.ignore_in_target') || 'Ignorar'}`
                          : conf.type === 'new_id_ref'
                            ? `⚡ ${conf.source}`
                            : conf.source
                              ? (conf.source.includes('.') ? conf.source.split('.').pop() : conf.source)
                              : (t('wizard.mapping.unmapped') || '-- Sin Mapear --')
                      }
                      triggerClass={
                        conf.type === 'new_id_ref'
                          ? 'border-amber-400 bg-amber-50 text-amber-800 font-bold'
                          : conf.type === 'ignore'
                            ? 'bg-slate-100 text-slate-500 border-slate-200'
                            : conf.confidence && conf.confidence < 50
                              ? 'border-red-400 bg-red-50 text-red-700 font-bold shadow-sm'
                              : !conf.source
                                ? 'border-amber-200 bg-amber-50'
                                : 'border-slate-200 bg-white'
                      }
                    />
                    {/* 🔧 confidence DESPUÉS del select: fluye abajo en mobile, absolute en desktop */}
                    {conf.confidence && conf.type !== 'ignore' && (
                      <div className="mt-0.5 md:mt-0 md:absolute md:right-8 md:top-1/2 md:-translate-y-1/2 z-10 flex justify-end md:justify-start">
                        <span className={`text-[9px] md:text-[10px] font-bold px-1.5 py-0.5 rounded-full border
                          ${conf.confidence >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : conf.confidence >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {conf.confidence}% {t('wizard.mapping.ai_label') || 'IA'}
                        </span>
                      </div>
                    )}
                    {sColObj && conf.type !== 'ignore' && (
                      <div className="flex items-center gap-2 mt-1 px-1">
                        {sColObj.is_pk && <span title={t('wizard.mapping.primary_key')} className="text-[10px]">🔑</span>}
                        {sColObj.is_fk && <span title={t('wizard.mapping.foreign_key')} className="text-[10px]">🔗</span>}
                        {/* {sColObj.type && <span className="text-[9px] text-slate-400 font-mono">[{sColObj.type}]</span>} */}
                        {sColObj.type && (
                          <span className="text-[9px] text-slate-400 font-mono">
                            [{sColObj.type}{sColObj.length ? `(${sColObj.length})` : ''}]
                          </span>
                        )}

                        {warnings.length > 0 && (
                          <div className="group/warnsrc relative ml-auto">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 cursor-help" />
                            <div className="absolute right-0 bottom-full mb-1 hidden group-hover/warnsrc:block w-48 bg-slate-800 text-white text-[10px] p-2 rounded shadow-lg z-50">
                              {warnings.map((w, i) => <p key={i}>{w}</p>)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isTargetFlatFile && (
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-2 text-[11px] font-medium text-slate-500 flex flex-wrap gap-6 rounded-b-2xl">
          <span className="flex items-center"><span className="mr-1.5 text-[12px]">🔑</span>{t('wizard.mapping.primary_key')}</span>
          <span className="flex items-center"><span className="mr-1.5 text-[12px]">🔗</span>{t('wizard.mapping.foreign_key')}</span>
          <span className="flex items-center"><span className="text-red-500 font-black text-sm mr-1.5">*</span>{t('wizard.mapping.mandatory_field_label') || 'Obligatorio'}</span>
        </div>
      )}
    </div>
  );
};